// Транспорт раздела «Смарт-процессы Bitrix» (/api/bx-smart/*): реестр 20 типов,
// дозеркаленных 31.07.2026, страницы записей из crm_items и одна запись с raw целиком.
// ТОЛЬКО ЧТЕНИЕ.
//
// ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. У семи старых типов смарт-процессов свои экраны со своими
// правами (дебиторка — отдельная дверь). Этот раздел — обзор двадцати НОВЫХ типов
// одним экраном; его форма ответа (raw целиком, счёты реестра) чужим экранам не
// нужна, а переиспользовать «покажи записи» через чужой транспорт значило бы
// однажды пронести данные мимо двери 'bx-smart'.
//
// Подписи полей раздел НЕ возит: их отдаёт существующая ручка /api/fields
// (хук useFields(`item:<тип>`) в api/hooks.ts) — тем же способом, что и
// экраны /smart/*.

import { useQuery } from '@tanstack/react-query'

import { fmtDate, fmtMoney } from '../lib/format'
import { CoreUnsupported, Unauthorized } from './client'
import type { FieldMeta } from './types'

const БАЗА = '/api/bx-smart'

/** Сколько строк на странице. То же число, что у ядра (СТРАНИЦА в bxs_registry_api). */
export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/coordProjects.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Смарт-процессы Bitrix» вашей роли не открыт')
    }
    // 400 здесь — это «такого типа в реестре нет» или кривой параметр. Пустая
    // таблица читалась бы как «записей нет», а это другой ответ.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────── формы ответов ядра (см. bxs_registry_api.py) ───────────────

export interface ТипРеестра {
  type: number
  title: string
  /** Живых записей в зеркале (NOT is_deleted). 0 — тип пуст, а не «типа нет». */
  count: number
}

export interface СтрокаТипа {
  id: number
  title: string | null
  stage_id: string | null
  category_id: number | null
  assigned_by_id: number | null
  opportunity: number | null
  currency: string | null
  updated_time: string | null
  /** Запись Bitrix целиком — приходит только при full=1 (наши страницы его просят:
   * по raw первой страницы подбираются колонки). */
  raw?: Record<string, unknown>
}

export interface СтраницаТипа {
  rows: СтрокаТипа[]
  total: number
  start: number
  limit: number
  next: number | null
  type: number
  title: string
}

export interface ОднаЗапись {
  row: СтрокаТипа & { raw: Record<string, unknown> }
  type: number
  title: string
}

// ─────────────── хуки ───────────────

export const useРеестр = () =>
  useQuery({
    queryKey: ['bx-smart', 'registry'],
    queryFn: () => запрос<{ rows: ТипРеестра[]; total: number }>('/registry'),
    staleTime: 5 * 60_000,
    retry: 1,
  })

export const useСтраница = (тип: number | null, поиск: string, start: number) =>
  useQuery({
    queryKey: ['bx-smart', 'rows', тип, поиск, start],
    queryFn: () =>
      запрос<СтраницаТипа>(
        `/rows?type=${тип}&start=${start}&full=1`
        + (поиск ? `&q=${encodeURIComponent(поиск)}` : ''),
      ),
    enabled: тип !== null,
    // предыдущая страница остаётся на экране, пока едет следующая — таблица не мигает
    placeholderData: (prev) => prev,
    staleTime: 60_000,
    retry: 1,
  })

export const useЗапись = (тип: number | null, id: number | null) =>
  useQuery({
    queryKey: ['bx-smart', 'row', тип, id],
    queryFn: () => запрос<ОднаЗапись>(`/row?type=${тип}&id=${id}`),
    enabled: тип !== null && id !== null,
    staleTime: 60_000,
    retry: 1,
  })

// ─────────────── чистые помощники (их проверяют тесты) ───────────────

/** Деньги Bitrix приезжают строкой «515000|RUB» — разбираем по форме, не по коду поля. */
export function деньгиИзСтроки(v: unknown): { сумма: number; валюта: string } | null {
  if (typeof v !== 'string') return null
  const м = /^(-?\d+(?:\.\d+)?)\|([A-Z]{3})$/.exec(v.trim())
  if (!м) return null
  return { сумма: Number(м[1]), валюта: м[2] }
}

/** Пусто ли значение raw. `0` и `'0'` — НЕ пустота: живые нули уже теряли (см. CLAUDE.md). */
export function пусто(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return true
  if (Array.isArray(v)) return v.length === 0
  if (typeof v === 'object') return Object.keys(v as object).length === 0
  return false
}

/**
 * Автоподбор колонок по заполненности ПЕРВОЙ страницы: свои поля типа (ufCrm*) с
 * наибольшей долей непустых значений. Состав UF-полей зашивать нельзя — коды у каждого
 * типа свои и меняются в Bitrix без нас (тот же довод, что у экранов /smart/*, только
 * там заполненность берётся из справочника, а здесь — из живых строк страницы:
 * fill_pct для этих типов ядро ещё не считало).
 */
export function выбратьКолонки(строки: СтрокаТипа[], максимум = 6): string[] {
  const счёт = new Map<string, number>()
  for (const р of строки) {
    const raw = р.raw
    if (!raw) continue
    for (const [к, v] of Object.entries(raw)) {
      // системные ключи (id, stageId, updatedTime…) уже показаны своими колонками
      if (!к.startsWith('ufCrm')) continue
      if (!пусто(v)) счёт.set(к, (счёт.get(к) ?? 0) + 1)
    }
  }
  return [...счёт.entries()]
    .sort((а, б) => б[1] - а[1] || а[0].localeCompare(б[0]))
    .slice(0, максимум)
    .map(([к]) => к)
}

/**
 * Значение поля raw — человеку. Деньги «N|RUB» разбираются по форме, перечисления —
 * по enum_map из справочника (значение записи — ID варианта), даты — по типу поля.
 * Пустое — прочерк.
 */
export function показатьЗначение(v: unknown, meta?: FieldMeta): string {
  if (пусто(v)) return '—'
  if (Array.isArray(v)) {
    return v.map((э) => показатьЗначение(э, meta)).filter((т) => т !== '—').join(', ') || '—'
  }
  const деньги = деньгиИзСтроки(v)
  if (деньги) return fmtMoney(деньги.сумма, деньги.валюта)
  const карта = meta?.enum_map
  if (карта && карта[String(v)] !== undefined) return карта[String(v)]
  if (meta?.type === 'date' || meta?.type === 'datetime') {
    return fmtDate(String(v), meta.type === 'datetime')
  }
  if (typeof v === 'boolean') return v ? 'да' : 'нет'
  if (meta?.type === 'boolean') return v === 'Y' || v === '1' ? 'да' : 'нет'
  if (typeof v === 'object') {
    // мультиполе или вложенный объект Bitrix — не прячем, но и не роняем экран
    try {
      return JSON.stringify(v)
    } catch {
      return String(v)
    }
  }
  return String(v)
}
